import {
    ChevronRightIcon,
    CursorArrowRaysIcon,
    EnvelopeOpenIcon,
    PaperClipIcon,
} from "@heroicons/react/24/outline";
import {
    formatNumber,
    relativeTimeAgo,
    SentMessageId,
    SentMessageType,
} from "@sp-crm/core";
import React from "react";
import { Link } from "react-router-dom";
import { useFeature } from "store/selectors/hooks";
import { GetMessageQuery, useGetMessageQuery } from "../../generated/graphql";
import { isoDateStringToHumanReadable } from "../../util/date/locale-date-format";
import { Spinner } from "../ui/spinner";
import {
    BouncedStatusMessage,
    DeliveredStatusMessage,
    NotDeliveredStatusMessage,
    SendingStatusMessage,
    SentStatusMessage,
} from "./status-message";

type MessageType = GetMessageQuery["getMessage"];

interface StatusProps {
    status: string;
    latestEventStatus: string | null | undefined;
    messageType: SentMessageType;
}

const StatusIndicator: React.FC<StatusProps> = props => {
    const { status, latestEventStatus, messageType } = props;

    if (status === "warning") {
        return <BouncedStatusMessage mode="icon" />;
    }
    if (status === "error") {
        return <NotDeliveredStatusMessage mode="icon" />;
    }
    if (status === "success") {
        return <DeliveredStatusMessage mode="icon" />;
    }
    if (
        latestEventStatus === "fax-send-failure" ||
        latestEventStatus === "fax-delivery-failure"
    ) {
        return <NotDeliveredStatusMessage mode="icon" />;
    }
    if (latestEventStatus === "fax-delivery-success") {
        return <DeliveredStatusMessage mode="icon" />;
    }
    if (!latestEventStatus && messageType === "ClientIntakeFax") {
        return <SendingStatusMessage mode="icon" />;
    }
    if (latestEventStatus === "email-send-success") {
        return <SentStatusMessage mode="icon" />;
    }

    return null;
};

interface OpenedEventsSectionProps {
    message: MessageType;
}

const OpenedEventsSection: React.FC<OpenedEventsSectionProps> = ({ message }) => {
    const openedEvents = message.events?.filter(e => e.eventType === "opened") ?? [];

    if (openedEvents.length === 0) {
        return null;
    }

    const hasRecipientInfo = openedEvents.some(o => !!o.recipient);

    return (
        <div className="pt-3 border-t border-gray-100">
            <div className="text-xs text-gray-500 font-medium mb-1 flex items-center gap-1.5">
                <EnvelopeOpenIcon className="h-3.5 w-3.5" />
                Opened ({openedEvents.length})
            </div>
            {hasRecipientInfo ? (
                <ul className="text-sm text-gray-600 space-y-0.5">
                    {openedEvents
                        .filter(o => !!o.recipient)
                        .map((event, index) => (
                            <li key={index} className="flex items-center gap-2">
                                <span className="text-gray-500">
                                    {isoDateStringToHumanReadable(event.eventTimestamp)}
                                </span>
                                <span className="text-gray-400">·</span>
                                <span>{event.recipient}</span>
                            </li>
                        ))}
                </ul>
            ) : (
                <ul className="text-sm text-gray-600 space-y-0.5">
                    {openedEvents.map((event, index) => (
                        <li key={index}>
                            {isoDateStringToHumanReadable(event.eventTimestamp)}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

interface ClickedLinksSectionProps {
    message: MessageType;
}

const ClickedLinksSection: React.FC<ClickedLinksSectionProps> = ({ message }) => {
    const clickedLinks = message.clickedLinkEvents ?? [];

    if (clickedLinks.length === 0) {
        return null;
    }

    return (
        <div className="pt-3 border-t border-gray-100">
            <div className="text-xs text-gray-500 font-medium mb-1 flex items-center gap-1.5">
                <CursorArrowRaysIcon className="h-3.5 w-3.5" />
                Links Clicked
            </div>
            <ul className="text-sm text-gray-600 space-y-1.5">
                {clickedLinks.map((cle, index) => {
                    const clicksByRecipient: Record<string, number> =
                        cle.eventsV2?.reduce(
                            (acc, e) => {
                                const key = e.recipient || "unknown";
                                acc[key] = (acc[key] || 0) + 1;
                                return acc;
                            },
                            {} as Record<string, number>,
                        ) || {};

                    const totalClicks = cle.eventsV2?.length || 0;
                    const hasRecipientInfo = cle.eventsV2?.some(e => !!e.recipient);

                    return (
                        <li key={index} className="space-y-0.5">
                            <div className="text-gray-700 truncate" title={cle.link}>
                                {cle.redacted ? (
                                    <span className="italic text-gray-400">
                                        (link redacted)
                                    </span>
                                ) : (
                                    cle.link
                                )}
                            </div>
                            {hasRecipientInfo ? (
                                <div className="text-xs text-gray-500 pl-2">
                                    {Object.entries(clicksByRecipient)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([recipient, count]) => (
                                            <span key={recipient} className="mr-3">
                                                {recipient} ({formatNumber(count)})
                                            </span>
                                        ))}
                                </div>
                            ) : (
                                <div className="text-xs text-gray-500 pl-2">
                                    {formatNumber(totalClicks)} click
                                    {totalClicks !== 1 ? "s" : ""}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

interface Props {
    messageId: SentMessageId;
    attachmentCount: number;
    status: string;
    latestEventStatus: string | null | undefined;
    messageType: SentMessageType;
    onCollapse: (() => void) | undefined;
    openedCount?: number;
    clickCount?: number;
    senderName?: string;
    headerActions?: React.ReactNode;
}

export const SentMessageInlineDetailLegacy: React.FC<Props> = props => {
    const {
        messageId,
        attachmentCount,
        status,
        latestEventStatus,
        messageType,
        onCollapse,
        openedCount = 0,
        clickCount = 0,
        senderName = "You",
        headerActions,
    } = props;
    const enhancedMode = useFeature("enhancedSentMessageView");
    const query = useGetMessageQuery({ id: messageId });

    if (query.isLoading) {
        return (
            <div className="border-b border-gray-200">
                <div
                    className={`flex items-center gap-3 px-5 py-2 ${
                        onCollapse ? "hover:bg-gray-50 cursor-pointer" : ""
                    }`}
                    onClick={onCollapse}>
                    {onCollapse ? (
                        <ChevronRightIcon
                            className="h-3 w-3 text-gray-400 flex-shrink-0 rotate-90"
                            aria-hidden="true"
                        />
                    ) : null}
                    <div className="flex-1">
                        <Spinner />
                    </div>
                </div>
            </div>
        );
    }

    if (query.isError || !query.data) {
        return (
            <div className="border-b border-gray-200">
                <div
                    className={`flex items-center gap-3 px-5 py-2 text-red-600 text-sm ${
                        onCollapse ? "hover:bg-gray-50 cursor-pointer" : ""
                    }`}
                    onClick={onCollapse}>
                    {onCollapse ? (
                        <ChevronRightIcon
                            className="h-3 w-3 text-gray-400 flex-shrink-0 rotate-90"
                            aria-hidden="true"
                        />
                    ) : null}
                    <span>Failed to load message</span>
                </div>
            </div>
        );
    }

    const message = query.data.getMessage;
    const timestamp = new Date(message.createdAt);

    return (
        <div className="border-b border-gray-200">
            <div
                className={`flex items-start gap-3 px-5 py-2 ${
                    onCollapse ? "hover:bg-gray-50 cursor-pointer" : ""
                }`}
                onClick={onCollapse}>
                {onCollapse ? (
                    <ChevronRightIcon
                        className="h-3 w-3 text-gray-400 flex-shrink-0 mt-1 rotate-90"
                        aria-hidden="true"
                    />
                ) : null}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm text-gray-900">
                            {senderName}
                        </span>
                        <div className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
                            {attachmentCount > 0 && (
                                <span className="inline-flex items-center gap-0.5">
                                    <PaperClipIcon className="h-3.5 w-3.5 text-gray-400" />
                                    {attachmentCount}
                                </span>
                            )}
                            {enhancedMode && (
                                <>
                                    <span className="inline-flex items-center gap-0.5">
                                        <EnvelopeOpenIcon className="h-3.5 w-3.5 text-gray-400" />
                                        {openedCount}
                                    </span>
                                    <span className="inline-flex items-center gap-0.5">
                                        <CursorArrowRaysIcon className="h-3.5 w-3.5 text-gray-400" />
                                        {clickCount}
                                    </span>
                                </>
                            )}
                            <StatusIndicator
                                messageType={messageType}
                                status={status}
                                latestEventStatus={latestEventStatus}
                            />
                            <span>{relativeTimeAgo(timestamp)}</span>
                            {headerActions}
                        </div>
                    </div>
                    <div className="text-sm text-gray-500">
                        <span className="text-gray-400">To:</span>{" "}
                        {(message.to ?? []).join(", ")}
                    </div>
                    {message.cc && message.cc.length > 0 ? (
                        <div className="text-sm text-gray-500">
                            <span className="text-gray-400">Cc:</span>{" "}
                            {message.cc.join(", ")}
                        </div>
                    ) : null}
                    <div className="text-sm text-gray-500">
                        <span className="text-gray-400">Sent:</span>{" "}
                        {isoDateStringToHumanReadable(message.createdAt)}
                    </div>
                </div>
            </div>
            <div className="pt-2 pb-3 pl-11 pr-5 space-y-3">
                {message.bouncedRecipients.length > 0 && (
                    <div className="text-sm text-yellow-700 bg-yellow-50 rounded p-2">
                        Could not deliver to: {message.bouncedRecipients.join(", ")}
                    </div>
                )}
                {message.invoice && (
                    <div className="text-sm text-gray-500">
                        <span className="text-gray-400">Invoice:</span>{" "}
                        <Link
                            to={`/billing/invoices/${message.invoice.id}`}
                            className="text-brand-600 hover:text-brand-800 hover:underline"
                            onClick={e => e.stopPropagation()}>
                            {message.invoice.serial}
                        </Link>
                    </div>
                )}
                <div
                    className="unreset text-sm text-gray-700"
                    dangerouslySetInnerHTML={{ __html: message.body ?? "" }}
                />
                {message.attachments && message.attachments.length > 0 && (
                    <div className="pt-3 border-t border-gray-100">
                        <div className="text-xs text-gray-500 font-medium mb-1">
                            Attachments
                        </div>
                        <ul className="text-sm text-gray-600 space-y-0.5">
                            {message.attachments.map((attachment, index) => (
                                <li
                                    key={index}
                                    className="flex items-center gap-1.5 truncate">
                                    <PaperClipIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                    {attachment}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {enhancedMode && (
                    <>
                        <OpenedEventsSection message={message} />
                        <ClickedLinksSection message={message} />
                    </>
                )}
            </div>
        </div>
    );
};
